<template>
  <div class="container py-5">

    <button
      class="button is-primary mb-5"
      @click="addingContact = true">
      Create a New Contact
    </button>

    <article 
      v-if="contacts.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no contacts.
      </div>
    </article>

    <table v-else class="table is-fullwidth is-hoverable is-clickable">
      <thead>
        <tr>
          <th 
            v-for="(header, headerIndex) in headers"
            :key="headerIndex">
            {{ header.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr 
          class="v-centered"
          v-for="contact in contacts"
          :key="contact.id">

          <template v-if="contact.id != editingContact">
            <td>
              <router-link 
                :to="{ 
                  name: 'TicketCompanyViewer', 
                  params: { companyId: contact.company_id }
                }">
                {{ contact.company.name }}
              </router-link>
            </td>
            <td>{{ contact.first_name }}</td>
            <td>{{ contact.last_name }}</td>
            <td>{{ contact.position }}</td>
            <td>{{ contact.phone1 }}</td>
            <td>{{ contact.email }}</td>
            <td>
              <div class="buttons buttons-right">
                <button 
                  class="button is-small"
                  @click="editingContact = contact.id">
                  <span class="icon">
                    <i class="fas fa-edit has-text-grey">
                    </i>
                  </span>
                </button>
                <button 
                  class="button is-small"
                  @click="launchConfirmDelete(contact.id)">
                  <span class="icon">
                    <i class="fas fa-trash has-text-grey">
                    </i>
                  </span>
                </button>
              </div>
            </td>
          </template>

          <template v-else>
            <td>
              <router-link 
                :to="{ 
                  name: 'TicketCompanyViewer', 
                  params: { companyId: contact.company_id }
                }">
                {{ contact.company.name }}
              </router-link>
            </td>
            <td>
              <v-text-field 
                v-model="contact.first_name">
              </v-text-field>
            </td>
            <td>
              <v-text-field 
                v-model="contact.last_name">
              </v-text-field>
            </td>
            <td>
              <v-text-field 
                v-model="contact.position">
              </v-text-field>
            </td>
            <td>
              <v-text-field 
                v-model="contact.phone1">
              </v-text-field>
            </td>
            <td>
              <v-text-field 
                required
                :rules="emailRules"
                v-model="contact.email">
              </v-text-field>
            </td>
            <td>
              <div class="buttons buttons-right">
                <button 
                  class="button is-small is-primary"
                  @click="saveChanges(contact)">
                  <span class="icon">
                    <i class="fas fa-save">
                    </i>
                  </span>
                </button>
                <button 
                  class="button is-small"
                  @click="editingContact = null">
                  <span class="icon">
                    <i class="fas fa-times has-text-grey">
                    </i>
                  </span>
                </button>
              </div>
            </td>
          </template>
        </tr>
      </tbody>
    </table>

    <add-contact-modal 
      v-model="addingContact"
      :companies="companies"/>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VPagination from '@/components/base/VPagination'
import VConfirmModal from '@/components/base/VConfirmModal'

import AddContactModal from '@/components/ticketing/AddContactModal'
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VTextField, VPagination, VConfirmModal, AddContactModal 
  },
  data() {
    return {
      headers: [
        { label: 'Company', field: 'company_id' },
        { label: 'First Name', field: 'first_name' },
        { label: 'Last Name', field: 'last_name' },
        { label: 'Position', field: 'position' },
        { label: 'Phone Number', field: 'phone1' },
        { label: 'Email', field: 'email' },
        { label: '' }
      ],
      editingContact: null,
      addingContact: false,
      emailRules: [
        v => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(v) || 'Invalid e-mail'
        }
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this contact?',
        type: 'danger'
      },
      deleteConfirmedCb: null
    }
  },
  computed: {
    ...mapState("tickets", {
      contacts: "contacts", 
      companies: "companies",
      pagination: "contactPagination"
    })
  },
  watch: {
    'pagination.page'(page) {
      this.getContacts({ page: page });
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("tickets", [
      "getContacts", "getCompanies", "deleteContact", "updateContact"
    ]),
    ...mapMutations("tickets", {
      changePage: "changeContactPage"
    }),
    initialize() {
      this.getContacts();    
      this.getCompanies();
    },
    saveChanges(contact) {
      this.updateContact({ contactId: contact.id, data: contact }).then(() => {
        this.editingContact = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteContact(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.state = true;
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
</style>